/**
 * ConfirmPanel — 확인창 공용 틀. useConfirm(ConfirmDialogProvider) 과 ReasonConfirmModal 이 함께 쓴다.
 *
 * 구조 (2026-10-13, Claude Design D안): [kind 색 질문형 제목] / 요약 한 줄 / 영향 목록 상자 / (추가 내용) / 버튼.
 *   - 영향 목록은 10개까지 보이고 나머지는 "외 N건 더 보기"로 펼친다(목록 상자 자체는 스크롤하지 않음).
 *   - 내용이 화면보다 길면 버튼 줄을 남기고 본문만 스크롤한다(MO 포함).
 *   - Esc 닫기(처리 중 제외)·Tab 가둠·열기 전 포커스 복원·배경 스크롤 잠금.
 */
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import type { ConfirmEffect, ConfirmKind } from "@/contexts/confirm-context";

const EFFECT_LIMIT = 10;
const STRONG_ICON: Record<ConfirmKind, string> = { danger: "trash", warning: "warning", info: "info" };

interface Props {
  kind: ConfirmKind;
  title: ReactNode;
  summary?: ReactNode;
  effects?: ConfirmEffect[];
  irreversible?: boolean;
  /** 영향 목록 아래 추가 내용(사유 입력·진행·결과 등) */
  children?: ReactNode;
  /** 버튼 줄 */
  actions: ReactNode;
  /** Esc·배경 클릭 — busy 중에는 호출하지 않는다 */
  onDismiss: () => void;
  busy?: boolean;
  /** 열릴 때 포커스할 요소. 없으면 패널 */
  initialFocus?: RefObject<HTMLElement | null>;
  /** 닫힌 뒤 원래 요소가 사라졌을 때 대신 포커스할 선택자 */
  fallbackFocus?: string;
  panelRef?: RefObject<HTMLDivElement | null>;
  role?: "dialog" | "alertdialog";
  className?: string;
}

export function ConfirmPanel({ kind, title, summary, effects = [], irreversible, children, actions, onDismiss, busy = false, initialFocus, fallbackFocus, panelRef, role = "alertdialog", className = "" }: Props) {
  const id = useId();
  const ownRef = useRef<HTMLDivElement>(null);
  const panel = panelRef ?? ownRef;
  const dismissRef = useRef(onDismiss);
  const busyRef = useRef(busy);
  useEffect(() => { dismissRef.current = onDismiss; busyRef.current = busy; });
  const dismiss = useBackdropDismiss(() => { if (!busyRef.current) dismissRef.current(); }, { disabled: busy });

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    (initialFocus?.current ?? panel.current)?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous?.isConnected && !previous.matches(":disabled")) previous.focus();
      else if (fallbackFocus) document.querySelector<HTMLElement>(fallbackFocus)?.focus();
    };
    // 열릴 때 한 번만 — 포커스 대상은 첫 렌더 기준
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // capture 단계에서 받아 가장 위에 뜬 확인창이 Esc 를 먼저 소비한다(뒤쪽 모달이 함께 닫히지 않게).
    const onKey = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      // portal 입력의 키 처리를 먼저 허용한다. 검색 메뉴의 Esc가 폼 전체를 닫으면 입력이 사라진다.
      if (target?.closest('.select-menu, .dp-popup, .dp-cs-menu')) return;
      if (event.key === "Escape" && target?.closest('.select.is-open, .date-picker input')) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) dismissRef.current();
      } else if (event.key === "Tab") {
        const nodes = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), textarea:not(:disabled), input:not(:disabled), summary, [tabindex="0"]') ?? [])
          .filter((node) => node.getClientRects().length > 0);
        const first = nodes[0], last = nodes[nodes.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!first) { event.preventDefault(); panel.current?.focus(); return; }
        if (event.shiftKey && (active === first || !nodes.includes(active as HTMLElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (active === last || !nodes.includes(active as HTMLElement))) {
          event.preventDefault(); first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [panel]);

  const hasEffects = irreversible || effects.length > 0;
  return createPortal(
    <div className="modal-backdrop confirm-backdrop" {...dismiss}>
      <div
        ref={panel}
        className={`modal-panel confirm-dialog confirm-${kind} ${className}`.trim()}
        role={role}
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={summary || hasEffects ? `${id}-desc` : undefined}
        aria-busy={busy || undefined}
        tabIndex={-1}
      >
        <div className="confirm-scroll">
          <h3 className="confirm-title" id={`${id}-title`}>{title}</h3>
          {(summary || hasEffects) && (
            <div className="confirm-desc" id={`${id}-desc`}>
              {summary && <div className="confirm-summary">{summary}</div>}
              {hasEffects && <EffectList kind={kind} effects={effects} irreversible={irreversible} />}
            </div>
          )}
          {children}
        </div>
        <div className="form-actions confirm-actions">{actions}</div>
      </div>
    </div>,
    document.body,
  );
}

function EffectList({ kind, effects, irreversible }: { kind: ConfirmKind; effects: ConfirmEffect[]; irreversible?: boolean }) {
  const [open, setOpen] = useState(false);
  const rows = effects.map((e) => (typeof e === "string" ? { text: e } : e));
  // "되돌릴 수 없습니다" 는 접히지 않도록 맨 앞에 둔다.
  if (irreversible) rows.unshift({ text: "되돌릴 수 없습니다.", strong: true, icon: "undo" });
  const hidden = rows.length > EFFECT_LIMIT && !open ? rows.length - EFFECT_LIMIT : 0;
  const shown = hidden ? rows.slice(0, EFFECT_LIMIT) : rows;
  return (
    <div className="confirm-effects">
      <ul>
        {shown.map((row, i) => (
          <li key={i} className={row.strong ? "is-strong" : undefined}>
            <span className="confirm-effect-mark" aria-hidden="true">
              {row.icon || row.strong ? <Icon name={row.icon ?? STRONG_ICON[kind]} size={16} style={{ strokeWidth: 2 }} /> : <span className="confirm-effect-dot" />}
            </span>
            <span className="confirm-effect-text">{row.text}</span>
          </li>
        ))}
      </ul>
      {rows.length > EFFECT_LIMIT && (
        <button type="button" className="confirm-effects-more" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? "접기" : `… 외 ${hidden}건 더 보기`}
        </button>
      )}
    </div>
  );
}
